import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useTranslation } from "@/hooks/useTranslation";

// ── Password strength helpers ─────────────────

interface PasswordCriterion {
  key: string;
  met: boolean;
}

function getPasswordCriteria(pw: string): PasswordCriterion[] {
  return [
    { key: "atLeast8", met: pw.length >= 8 },
    { key: "uppercase", met: /[A-Z]/.test(pw) },
    { key: "lowercase", met: /[a-z]/.test(pw) },
    { key: "number", met: /\d/.test(pw) },
    { key: "special", met: /[^A-Za-z0-9]/.test(pw) },
  ];
}

function PasswordStrength({ password }: { password: string }) {
  const { t } = useTranslation();
  const criteria = getPasswordCriteria(password);
  const metCount = criteria.filter((c) => c.met).length;
  const strength =
    metCount <= 1
      ? "weak"
      : metCount <= 3
        ? "fair"
        : metCount === 4
          ? "good"
          : "strong";

  const barColors: Record<string, string> = {
    weak: "var(--danger)",
    fair: "var(--warning)",
    good: "var(--k-orange)",
    strong: "var(--success)",
  };

  if (!password) return null;

  return (
    <div className="flex flex-col gap-2 mt-2">
      {/* Strength bar */}
      <div className="flex items-center gap-2">
        <div className="flex flex-1 gap-1">
          {[1, 2, 3, 4, 5].map((i) => (
            <div
              key={i}
              className="h-1.5 flex-1 rounded-full transition-all"
              style={{
                background:
                  i <= metCount ? barColors[strength] : "var(--border-default)",
              }}
            />
          ))}
        </div>
        <span
          className="text-xs font-medium"
          style={{ color: barColors[strength] }}
        >
          {t(`auth.passwordStrength.${strength}`)}
        </span>
      </div>
      {/* Requirements checklist */}
      <ul className="flex flex-col gap-0.5">
        {criteria.map((c, i) => (
          <li
            key={i}
            className={cn("flex items-center gap-1.5 text-xs")}
            style={{ color: c.met ? "var(--success)" : "var(--text-muted)" }}
          >
            <span>{c.met ? "✓" : "○"}</span>
            {t(`auth.passwordStrength.${c.key}`)}
          </li>
        ))}
      </ul>
    </div>
  );
}

// ── ResetPassword page ────────────────────────

export function ResetPassword() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const criteria = getPasswordCriteria(password);
  const allMet = criteria.filter((c) => c.met).length >= 4; // at least "good"

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (password !== confirm) {
      setError(t("auth.passwordsDoNotMatch"));
      return;
    }

    if (!token) {
      setError(t("auth.resetTokenMissing"));
      return;
    }

    setLoading(true);
    try {
      const res = await fetch("/api/auth/reset-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ token, newPassword: password }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(
          (data as { error?: { message?: string } })?.error?.message ??
            t("errors.generic")
        );
      }
      setSuccess(true);
      // Redirect to login after 2 seconds
      setTimeout(() => navigate("/login"), 2000);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t("errors.generic")
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center"
      style={{
        background:
          "radial-gradient(ellipse at center, var(--bg-card) 0%, var(--bg-page) 70%)",
      }}
    >
      <div
        className="w-full max-w-md p-8 rounded-lg"
        style={{
          background: "var(--bg-card)",
          border: "1px solid var(--border-default)",
        }}
      >
        <div className="text-center mb-8">
          <h1
            className="font-mono text-2xl font-medium tracking-[3px]"
            style={{ color: "var(--text-primary)" }}
          >
            KIBORG
          </h1>
          <p className="mt-2 text-sm" style={{ color: "var(--text-muted)" }}>
            {t("auth.setNewPassword")}
          </p>
        </div>

        {success ? (
          <div className="text-center space-y-4">
            <div
              className="p-4 rounded text-sm"
              style={{
                background: "var(--success-bg)",
                color: "var(--success)",
                border: "1px solid var(--success)",
              }}
            >
              {t("auth.passwordResetSuccess")}
            </div>
          </div>
        ) : (
          <>
            {error && (
              <div
                className="mb-4 p-3 rounded text-sm"
                style={{
                  background: "var(--danger-bg)",
                  color: "var(--danger)",
                  border: "1px solid var(--danger)",
                }}
              >
                {error}
              </div>
            )}

            {!token && (
              <div
                className="mb-4 p-3 rounded text-sm"
                style={{
                  background: "var(--warning-bg)",
                  color: "var(--warning)",
                  border: "1px solid var(--warning)",
                }}
              >
                {t("auth.noResetToken")}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label
                  className="block text-sm font-medium mb-1"
                  style={{ color: "var(--text-secondary)" }}
                >
                  {t("auth.newPassword")}
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t("auth.enterNewPassword")}
                    required
                    className="w-full px-3 py-2 rounded-md text-sm outline-none focus:ring-2"
                    style={{
                      background: "var(--bg-input)",
                      color: "var(--text-primary)",
                      border: "1px solid var(--border-default)",
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-xs"
                    style={{ color: "var(--text-muted)" }}
                  >
                    {showPassword ? t("auth.hide") : t("auth.show")}
                  </button>
                </div>
                <PasswordStrength password={password} />
              </div>

              <div>
                <label
                  className="block text-sm font-medium mb-1"
                  style={{ color: "var(--text-secondary)" }}
                >
                  {t("auth.confirmPassword")}
                </label>
                <div className="relative">
                  <input
                    type={showConfirm ? "text" : "password"}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    placeholder={t("auth.confirmNewPassword")}
                    required
                    className="w-full px-3 py-2 rounded-md text-sm outline-none focus:ring-2"
                    style={{
                      background: "var(--bg-input)",
                      color: "var(--text-primary)",
                      border: "1px solid var(--border-default)",
                      borderColor:
                        confirm && confirm !== password
                          ? "var(--danger)"
                          : undefined,
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirm(!showConfirm)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-xs"
                    style={{ color: "var(--text-muted)" }}
                  >
                    {showConfirm ? t("auth.hide") : t("auth.show")}
                  </button>
                </div>
                {confirm && confirm !== password && (
                  <p className="text-xs mt-1" style={{ color: "var(--danger)" }}>
                    {t("auth.passwordsDoNotMatch")}
                  </p>
                )}
              </div>

              <button
                type="submit"
                disabled={loading || !allMet || password !== confirm}
                className="w-full py-2 rounded-md text-sm font-medium transition-colors"
                style={{
                  background: "var(--accent)",
                  color: "#fff",
                  opacity:
                    loading || !allMet || password !== confirm ? 0.5 : 1,
                  cursor:
                    loading || !allMet || password !== confirm
                      ? "not-allowed"
                      : "pointer",
                }}
              >
                {loading ? t("auth.resettingPassword") : t("auth.resetPassword")}
              </button>
            </form>

            <div className="mt-4 text-center">
              <Link
                to="/login"
                className="text-sm"
                style={{ color: "var(--accent)" }}
              >
                {t("auth.backToSignIn")}
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
